import React from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * SectionTitle — semantic section-heading primitive (DR-4).
 *
 * Renders an h2/h3/h4 with canonical section-title styling. Accepts JSX
 * children (unlike PageHeader's string-only `title`). For card/panel headings
 * and embedded section headers — NOT page-level (use PageHeader for that).
 *
 * Optional `action` slot places a right-aligned affordance (e.g. a "View all"
 * link or "+ Add" button) on the same baseline as the heading text.
 */

export interface SectionTitleProps {
  /** Semantic heading level. Default h2. */
  as?: 'h2' | 'h3' | 'h4';
  children: React.ReactNode;
  /** Right-aligned action slot (e.g. a link or icon button). */
  action?: React.ReactNode;
  className?: string;
}

export function SectionTitle({
  as: Tag = 'h2',
  children,
  action,
  className,
}: SectionTitleProps) {
  const heading = (
    <Tag
      className={twMerge(
        'text-base font-semibold text-text-primary leading-snug',
        !action && 'mb-3',
        className,
      )}
    >
      {children}
    </Tag>
  );

  if (!action) return heading;

  return (
    <div className="flex items-center justify-between gap-2 mb-3">
      {heading}
      <div className="shrink-0">{action}</div>
    </div>
  );
}

SectionTitle.displayName = 'SectionTitle';
